"use client"

import { createContext, useContext } from "react"

const ApartContext = createContext()

export const useApartContext = () => {
  const context = useContext(ApartContext)
  if (!context) {
    throw new Error("useApartContext must be used within an ApartContextProvider")
  }
  return context
}

const ApartContextProvider = ({ children }) => {
  const apartmentData = {
    id: 3,
    titleEn: "✨J Tower Apartment✨",
    titleEs: "✨J Tower Apartment✨",
    titleFr: "✨J Tower Apartment✨",
    titleHe: "✨J Tower✨",
    internalCode: "(H10)",
    descriptionEn: "Stylish 1-Bedroom Apartment in Jerusalem's Cultural District – Perfect for Couples and Solo Travelers.",
    descriptionEs: "Elegante apartamento de 1 dormitorio en el distrito cultural de Jerusalén – Perfecto para parejas y viajeros solitarios.",
    descriptionFr: "Élégant appartement d'une chambre dans le quartier culturel de Jérusalem – Parfait pour les couples et les voyageurs en solo.",
    descriptionHe: "דירת חדר שינה אחת מעוצבת בלב אזור התרבות של ירושלים – מושלמת לזוגות ולטיילים יחידים.",
    longDescriptionEn: "Experience the perfect blend of modern comfort and historic charm in this beautifully renovated 1-bedroom apartment in Jerusalem.\n\nThis contemporary urban loft features sleek design elements with warm touches that make it feel like home. The open-concept living space includes a fully equipped kitchen, comfortable dining area, and a cozy lounge space perfect for relaxing after a day of exploring the city.\n\nThe bedroom offers a premium queen-size bed with luxury linens, while the modern bathroom includes a rainfall shower and premium toiletries. Every detail has been carefully considered to ensure your stay is both comfortable and memorable.",
    longDescriptionEs: "Experimente la perfecta combinación de comodidad moderna y encanto histórico en este apartamento de 1 dormitorio bellamente renovado en Jerusalén.\n\nEste loft urbano contemporáneo presenta elementos de diseño elegante con toques cálidos que lo hacen sentir como en casa. El espacio de vida de concepto abierto incluye una cocina totalmente equipada, un cómodo comedor y un acogedor espacio de estar perfecto para relajarse después de un día explorando la ciudad.\n\nEl dormitorio ofrece una cama queen-size premium con ropa de cama de lujo, mientras que el baño moderno incluye una ducha de lluvia y artículos de tocador premium. Cada detalle ha sido cuidadosamente considerado para asegurar que su estancia sea cómoda y memorable.",
    longDescriptionFr: "Découvrez le mélange parfait de confort moderne et de charme historique dans cet appartement d'une chambre magnifiquement rénové à Jérusalem.\n\nCe loft urbain contemporain présente des éléments de design élégants avec des touches chaleureuses qui le font ressembler à un chez-soi. L'espace de vie à concept ouvert comprend une cuisine entièrement équipée, une salle à manger confortable et un espace salon confortable parfait pour se détendre après une journée d'exploration de la ville.\n\nLa chambre offre un lit queen-size haut de gamme avec du linge de luxe, tandis que la salle de bain moderne comprend une douche à effet pluie et des articles de toilette haut de gamme. Chaque détail a été soigneusement considéré pour garantir que votre séjour soit à la fois confortable et mémorable.",
    longDescriptionHe: "תחוו שילוב מושלם של נוחות מודרנית וקסם היסטורי בדירה מהממת זו בירושלים, ששופצה בקפידה.\n\nהדירה המעוצבת כוללת מטבח מאובזר, פינת אוכל נעימה, וסלון אינטימי מושלם למנוחה אחרי יום טיולים בעיר.\n\nבחדר השינה תיהנו ממיטה זוגית מפנקת עם מצעים איכותיים, ובחדר הרחצה תמצאו מקלחת גשם יוקרתית ומוצרי רחצה מפנקים. כל פרט ופרט נבחר בקפידה כדי להפוך את השהות שלכם לנוחה ובלתי נשכחת.",
    size: 50,
    guests: 2,
    bedrooms: 1,
    bathrooms: 1,
    images: [
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569697/Apart_3_6_kbbgf1.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569693/Apart_3_1_hlraok.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569694/Apart_3_2_lrckk0.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569695/Apart_3_3_vrybmh.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569695/Apart_3_4_g64nsg.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569696/Apart_3_5_qlnxys.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569698/Apart_3_7_y1zlkt.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569699/Apart_3_8_jdwxw7.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569700/Apart_3_9_is9y9g.png"
    ],
    amenities: [
      { name: "Fully Equipped Kitchen", nameEs: "Cocina Totalmente Equipada", nameFr: "Cuisine Entièrement Équipée", nameHe: "מטבח מאובזר", icon: "kitchen" },
      { name: "Air Conditioning", nameEs: "Aire Acondicionado", nameFr: "Climatisation", nameHe: "מזגן", icon: "air" },
      { name: "Smart TV with Netflix", nameEs: "Smart TV con Netflix", nameFr: "Smart TV avec Netflix", nameHe: "טלוויזיה חכמה עם נטפליקס", icon: "tv" },
      { name: "High-Speed Wi-Fi", nameEs: "Wi-Fi de Alta Velocidad", nameFr: "Wi-Fi Haut Débit", nameHe: "אינטרנט אלחוטי מהיר", icon: "wifi" },
      { name: "Washing Machine", nameEs: "Lavadora", nameFr: "Machine à Laver", nameHe: "מכונת כביסה", icon: "washer" },
      { name: "Premium Coffee Machine", nameEs: "Cafetera Premium", nameFr: "Machine à Café Premium", nameHe: "מכונת קפה יוקרתית", icon: "coffee" },
      { name: "Luxury Linens & Towels", nameEs: "Ropa de Cama y Toallas de Lujo", nameFr: "Linge de Lit et Serviettes de Luxe", nameHe: "מצעים ומגבות יוקרתיים", icon: "bed" },
      { name: "Rainfall Shower", nameEs: "Ducha de Lluvia", nameFr: "Douche à Effet Pluie", nameHe: "מקלחת גשם", icon: "shower" },
      { name: "Workspace with Desk", nameEs: "Espacio de Trabajo con Escritorio", nameFr: "Espace de Travail avec Bureau", nameHe: "פינת עבודה עם שולחן", icon: "desk" },
      { name: "Bluetooth Speaker", nameEs: "Altavoz Bluetooth", nameFr: "Haut-parleur Bluetooth", nameHe: "רמקול בלוטות'", icon: "speaker" },
      { name: "Hairdryer", nameEs: "Secador de Pelo", nameFr: "Sèche-cheveux", nameHe: "מייבש שיער", icon: "hairdryer" },
      { name: "Iron & Ironing Board", nameEs: "Plancha y Tabla de Planchar", nameFr: "Fer et Planche à Repasser", nameHe: "מגהץ וקרש גיהוץ", icon: "iron" }
    ],
    location: {
      addressEn: "103 Jaffa Street, Jerusalem",
      addressEs: "103 Calle Jaffa, Jerusalén",
      addressFr: "103 Rue Jaffa, Jérusalem",
      addressHe: "רחוב יפו 103, ירושלים",
      addressDescEn: "In the Cultural District, Jerusalem",
      addressDescEs: "En el Distrito Cultural, Jerusalén",
      addressDescFr: "Dans le Quartier Culturel, Jérusalem",
      addressDescHe: "באזור התרבות, ירושלים",
      coordinates: {
        lat: 31.7857,
        lng: 35.2124
      },
      highlights: [
        {
          nameEn: "5-minute walk to Jerusalem's Museum District",
          nameEs: "A 5 minutos a pie del Distrito de Museos de Jerusalén",
          nameFr: "À 5 minutes à pied du quartier des musées de Jérusalem",
          nameHe: "5 דקות הליכה לאזור המוזיאונים של ירושלים"
        },
        {
          nameEn: "10-minute walk to the Old City",
          nameEs: "A 10 minutos a pie de la Ciudad Vieja",
          nameFr: "À 10 minutes à pied de la Vieille Ville",
          nameHe: "10 דקות הליכה לעיר העתיקה"
        },
        {
          nameEn: "Surrounded by art galleries, cafés, and boutiques",
          nameEs: "Rodeado de galerías de arte, cafés y boutiques",
          nameFr: "Entouré de galeries d'art, cafés et boutiques",
          nameHe: "מוקף בגלריות אמנות, בתי קפה ובוטיקים"
        },
        {
          nameEn: "Light rail station just 2 blocks away",
          nameEs: "Estación de tren ligero a solo 2 cuadras",
          nameFr: "Station de tramway à seulement 2 pâtés de maisons",
          nameHe: "תחנת הרכבת הקלה במרחק של שני בלוקים בלבד"
        },
        {
          nameEn: "Numerous dining options within walking distance",
          nameEs: "Numerosas opciones gastronómicas a poca distancia",
          nameFr: "Nombreuses options de restauration à distance de marche",
          nameHe: "מגוון מסעדות בטווח הליכה"
        }
      ]
    }
  };

  return (
    <ApartContext.Provider value={{ apartmentData }}>
      {children}
    </ApartContext.Provider>
  )
}

export default ApartContextProvider
